import _ from 'lodash';
import React from 'react';
import {connect} from 'react-redux';
import classNames from 'classnames';
import {push} from 'react-router-redux';

import Page from 'components/Page';
import Money from 'components/Money';
import Loading from 'components/Loading';
import ErrorBlock from 'components/ErrorBlock';
import Collapsible from 'components/Collapsible';
import DetailsContent from 'components/DetailsContent';
import BookingCreateModal from 'components/BookingCreateModal';
import DepositSummaryStatus from 'components/DepositSummaryStatus';
import {ConfirmationModal, OneOffChargeModal} from 'components/CommonModals';

import {
  InvoiceDetailModal,
  CreditNoteDetailModal,
  PaymentDetailModal,
  OverpaymentDetailModal,
  RefundDetailModal,
  InvoiceCreateModal,
  CreditNoteCreateModal,
  OverpaymentCreateModal,
  GocardlessCustomerDetailModal,
  GocardlessPaymentDetailModal,
  InitiateGoCardlessPaymentModal,
  CancelGocardlessCustomerModal,
  ConfirmPaymentModal
} from 'components/FinanceModals';

import {
  PrimaryTenantBox,
  BookingsTable,
  ChargesTable,
  SelectBookingModal,
  BulkInvoiceVoidModal,
  RoomChecksTimeline,
  ArrivalRoomChecksTable,
  DepartureRoomChecksTable,
  DepositSummaryTable,
  AddArrivalRoomCheckModal,
  AddDepartureRoomCheckModal,
  ArrivalRoomCheckDetailModal,
  DepartureRoomCheckDetailModal,
  ConfirmAddDepartureRoomCheckModal,
  DepositSummaryDetailModal
} from './components';

import {
  openModal,
  isModalOpen,
  closeModal,
  MODALS as BASE_MODALS
} from 'ducks/modals';
import {requiresPermission, PERMISSIONS} from 'ducks/auth';

import {
  fetchBookingSet,
  selectBookingSet,
  selectBookingSetIsLoading,
  fetchBookingSetsFinanceData,
  selectBookingSetsFinanceItems,
  selectBookingSetsFinanceTotals,
  MODALS,
  selectBookingSetsFinanceDataIsLoading,
  fetchBookingSetDepositFinanceData,
  selectBookingSetDepositFinanceItems,
  selectBookingSetDepositFinanceDataIsLoading,
  selectBookingSetDepositFinanceTotals,
  fetchArrivalRoomChecks,
  selectArrivalRoomChecksForBookingSet,
  fetchDepartureRoomChecks,
  selectDepartureRoomChecksForBookingSet,
  fetchDepositSummary,
  selectDepositSummary,
  fetchRoomChecksTimeline,
  selectRoomChecksTimeline
} from 'ducks/bookingSets';

import {
  SCHEDULE_ITEM_TYPES,
  MODALS as TENANCY_MODALS,
  SCHEDULE_TYPES
} from 'ducks/tenancy';

import {reverse} from 'utils/urls';
import {openModalFromUrl} from 'utils/modals';
import {BOOKING_DETAIL_URL} from 'config/urls';

import './styles.css';

const BOOKINGS = 'Bookings';
const CHARGES_AND_PAYMENTS = 'Charges & Payments';
const ROOM_CHECKS = 'Room Checks';

const TABS = [BOOKINGS, CHARGES_AND_PAYMENTS, ROOM_CHECKS];

const TabNavigation = ({selectedTab, selectTab}) => (
  <div className="TabNavigation colab-centered-v">
    {TABS.map(tab => (
      <div
        className={classNames(
          'TabNavigation__tab colab-centered-v colab-clickable',
          {
            'TabNavigation__tab--active': selectedTab == tab
          }
        )}
        key={tab}
        onClick={() => selectTab(tab)}>
        {tab}
      </div>
    ))}
  </div>
);

const BalanceHeader = ({currentBalance, totalBalance}) => (
  <div className="BalanceHeader colab-sidepadding-2">
    <div>
      <span className="BalanceHeader__title colab-bold">Current Balance</span>
      <span className="BalanceHeader__title colab-bold">Total Balance</span>
    </div>
    <div>
      <Money semibold amount={currentBalance} />
      <Money semibold amount={totalBalance} />
    </div>
  </div>
);

class BookingSetDetail extends React.Component<any> {
  state = {
    selectedTab: BOOKINGS
  };

  componentDidMount() {
    const {bookingSetId, locationRef} = this.props.match.params;

    const pageModals = {
      ...MODALS,
      ...BASE_MODALS,
      ...TENANCY_MODALS
    };

    openModalFromUrl(pageModals, this.props.openModal);

    this.props.requiresPermission(
      {
        locationRef,
        permission: PERMISSIONS.VIEW_BOOKING
      },
      () => this.props.fetchBookingSet({bookingSetId})
    );

    this.props.fetchBookingSetsFinanceData({bookingSetId});
    this.props.fetchBookingSetDepositFinanceData({bookingSetId});

    this.props.fetchArrivalRoomChecks({bookingSetId});
    this.props.fetchDepartureRoomChecks({bookingSetId});
    this.props.fetchDepositSummary({bookingSetId});
    this.props.fetchRoomChecksTimeline({bookingSetId});
  }

  openCreateBookingModal = () =>
    this.props.requiresPermission(
      {
        locationRef: this.props.match.params.locationRef,
        permission: PERMISSIONS.CREATE_BOOKING
      },
      () =>
        this.props.openModal(BASE_MODALS.CREATE_BOOKING, {
          initialBookingSet: this.props.bookingSet
        })
    );

  createBookingExtraActions = () => [
    () => closeModal(BASE_MODALS.CREATE_BOOKING),
    () => fetchBookingSet({bookingSetId: this.props.match.params.bookingSetId}),
    () =>
      fetchDepositSummary({bookingSetId: this.props.match.params.bookingSetId})
  ];

  redirectToBookingDetail = ({bookingId}) => {
    const {locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {permission: PERMISSIONS.VIEW_BOOKING, locationRef},
      () => {
        this.props.push(
          reverse(BOOKING_DETAIL_URL, {
            locationRef,
            bookingId
          })
        );
      }
    );
  };

  selectTab = selectedTab => {
    this.setState({selectedTab});
  };

  handleRowClick = ({item, itemId, scheduleType, tenancyId}) => {
    this.props.requiresPermission(
      {
        locationRef: this.props.match.params.locationRef,
        permission: PERMISSIONS.VIEW_SCHEDULE_ITEM_DETAILS
      },
      () => {
        switch (item) {
          case SCHEDULE_ITEM_TYPES.INVOICE:
            this.openInvoiceDetailModal(scheduleType, itemId, tenancyId);
            break;
          case SCHEDULE_ITEM_TYPES.CREDIT_NOTE:
            this.openCreditNoteDetailModal(scheduleType, itemId, tenancyId);
            break;
          case SCHEDULE_ITEM_TYPES.PAYMENT:
            this.openPaymentDetailModal(scheduleType, itemId, tenancyId);
            break;
          case SCHEDULE_ITEM_TYPES.OVERPAYMENT:
            this.openOverpaymentDetailModal(scheduleType, itemId, tenancyId);
            break;
          case SCHEDULE_ITEM_TYPES.REFUND:
            this.openRefundDetailModal(scheduleType, itemId, tenancyId);
            break;
          default:
            return;
        }
      }
    );
  };

  openInvoiceDetailModal = (scheduleType, invoiceId, tenancyId) => {
    const {match: {params: {locationRef}}} = this.props;

    this.props.openModal(
      TENANCY_MODALS.INVOICE_DETAIL,
      {
        scheduleType,
        invoiceId,
        locationRef,
        tenancyId
      },
      true
    );
  };

  openCreditNoteDetailModal = (scheduleType, creditNoteId, tenancyId) => {
    const {locationRef} = this.props.match.params;

    this.props.openModal(
      TENANCY_MODALS.CREDIT_NOTE_DETAIL,
      {
        scheduleType,
        creditNoteId,
        locationRef,
        tenancyId
      },
      true
    );
  };

  openOverpaymentDetailModal = (scheduleType, overpaymentId, tenancyId) => {
    const {locationRef} = this.props.match.params;

    this.props.openModal(
      TENANCY_MODALS.OVERPAYMENT_DETAIL,
      {
        scheduleType,
        overpaymentId,
        locationRef,
        tenancyId
      },
      true
    );
  };

  openPaymentDetailModal = (scheduleType, paymentId, tenancyId) => {
    const {match: {params: {locationRef}}} = this.props;

    this.props.openModal(
      TENANCY_MODALS.PAYMENT_DETAIL,
      {
        scheduleType,
        paymentId,
        locationRef,
        tenancyId
      },
      true
    );
  };

  openRefundDetailModal = (scheduleType, refundId, tenancyId) => {
    const {match: {params: {locationRef}}} = this.props;

    this.props.openModal(
      TENANCY_MODALS.REFUND_DETAIL,
      {
        scheduleType,
        refundId,
        locationRef,
        tenancyId
      },
      true
    );
  };

  openInvoiceCreateModal = ({booking}) => {
    const {match: {params: {locationRef}}} = this.props;

    this.props.requiresPermission(
      {
        locationRef: this.props.match.params.locationRef,
        permission: PERMISSIONS.CREATE_INVOICE
      },
      () => {
        this.props.openModal(TENANCY_MODALS.INVOICE_CREATE, {
          locationRef,
          booking,
          bookingId: booking.id,
          tenancyId: booking.tenancyId,
          primaryTenant: booking.primaryTenant,
          openFromBookingSet: true
        });
      }
    );
  };

  openCreditNoteCreateModal = ({booking}) => {
    const {match: {params: {locationRef}}} = this.props;

    this.props.requiresPermission(
      {
        locationRef: this.props.match.params.locationRef,
        permission: PERMISSIONS.CREATE_CREDIT_NOTE
      },
      () => {
        this.props.openModal(TENANCY_MODALS.CREDIT_NOTE_CREATE, {
          locationRef,
          tenancyId: booking.tenancyId,
          primaryTenant: booking.primaryTenant,
          booking,
          openFromBookingSet: true
        });
      }
    );
  };

  openOverpaymentCreateModal = ({booking}) => {
    const {match: {params: {locationRef}}} = this.props;

    this.props.requiresPermission(
      {
        locationRef,
        permission: PERMISSIONS.CREATE_OVERPAYMENT
      },
      () => {
        this.props.openModal(TENANCY_MODALS.OVERPAYMENT_CREATE, {
          locationRef,
          tenancyId: booking.tenancyId,
          primaryTenant: booking.primaryTenant,
          openFromBookingSet: true
        });
      }
    );
  };

  openSelectBookingModal = ({createItem}) => {
    const {match: {params: {locationRef, bookingSetId}}} = this.props;

    let modalHeading = '';

    switch (createItem) {
      case SCHEDULE_ITEM_TYPES.INVOICE:
        modalHeading = 'Create an invoice';
        break;
      case SCHEDULE_ITEM_TYPES.CREDIT_NOTE:
        modalHeading = 'Create a credit note';
        break;
      case SCHEDULE_ITEM_TYPES.OVERPAYMENT:
        modalHeading = 'Create an overpayment';
        break;
    }

    this.props.openModal(MODALS.SELECT_BOOKING, {
      createItem,
      modalHeading,
      locationRef,
      bookingSetId
    });
  };

  openCreateItemModal = ({booking, createItem}) => {
    switch (createItem) {
      case SCHEDULE_ITEM_TYPES.INVOICE:
        this.openInvoiceCreateModal({booking});
        break;
      case SCHEDULE_ITEM_TYPES.CREDIT_NOTE:
        this.openCreditNoteCreateModal({booking});
        break;
      case SCHEDULE_ITEM_TYPES.OVERPAYMENT:
        this.openOverpaymentCreateModal({booking});
        break;
    }
  };

  openBulkInvoiceVoidModal = () => {
    const {bookingSetId, locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {
        locationRef,
        permission: PERMISSIONS.VOID_SCHEDULE_ITEMS
      },
      () =>
        this.props.openModal(MODALS.BULK_INVOICE_VOID, {
          bookingSetId,
          locationRef
        })
    );
  };

  // This function will be deleted when bulk void for deposit schedule is implemented
  refetchChargesAndPaymentsExtraActions = () => {
    const {match: {params: {bookingSetId}}} = this.props;

    return [
      () => fetchBookingSetsFinanceData({bookingSetId}),
      () => fetchBookingSet({bookingSetId})
    ];
  };

  financeRefetchExtraActions = ({scheduleType}) => {
    const {match: {params: {bookingSetId}}} = this.props;

    let refetchExtraAction = fetchBookingSetsFinanceData;

    if (scheduleType == SCHEDULE_TYPES.DEPOSIT) {
      refetchExtraAction = fetchBookingSetDepositFinanceData;
    }

    return [
      () => refetchExtraAction({bookingSetId}),
      () => fetchBookingSet({bookingSetId}),
      () => fetchDepositSummary({bookingSetId}),
      () => fetchRoomChecksTimeline({bookingSetId})
    ];
  };

  financeDelayedExtraActions = () => {
    return [];
    // Do we need delayed refetch? We don't show if the items are synced in Xero..
    // const {match: {params: {bookingSetId}}} = this.props;
    // return [() => fetchBookingSetsFinanceData({bookingSetId})];
  };

  openConfirmPaymentModal = invoice => {
    const finances = [
      ...this.props.financeItems,
      ...this.props.depositFinanceItems
    ];

    const invoiceBookingId = finances
      .filter(item => item.id == invoice.id)
      .map(invoice => invoice.bookingId);

    if (_.isEmpty(invoiceBookingId)) {
      return;
    }

    const booking = _.get(
      this.props.bookingSet.bookings.filter(
        booking => booking.id == invoiceBookingId[0]
      ),
      '[0]'
    );

    const bookingData = {
      period: {
        startDate: booking.startDate,
        endDate: booking.endDate
      },
      status: booking.status,
      room: booking.room,
      primaryTenant: this.props.bookingSet.tenant
    };

    const invoiceDescription = _.get(invoice, 'items[0].description');

    this.props.openModal(TENANCY_MODALS.CONFIRM_PAYMENT, {
      invoiceDescription,
      booking: bookingData
    });
  };

  openAddArrivalRoomCheckModal = () => {
    const {bookingSetId, locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {
        locationRef,
        permission: PERMISSIONS.ADD_ROOM_CHECK
      },
      () =>
        this.props.openModal(MODALS.ADD_ARRIVAL_ROOM_CHECK, {
          bookingSetId,
          locationRef
        })
    );
  };

  openAddDepartureRoomCheckModal = () => {
    const {bookingSetId, locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {
        locationRef,
        permission: PERMISSIONS.ADD_ROOM_CHECK
      },
      () =>
        this.props.openModal(MODALS.ADD_DEPARTURE_ROOM_CHECK, {
          bookingSetId,
          locationRef
        })
    );
  };

  openDepositSummaryDetailModal = () => {
    const {bookingSetId, locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {
        locationRef,
        permission: PERMISSIONS.EDIT_DEPOSIT_SUMMARY
      },
      () =>
        this.props.openModal(MODALS.DEPOSIT_SUMMARY_DETAIL_MODAL, {
          bookingSetId,
          locationRef
        })
    );
  };

  openArrivalRoomCheckDetailModal = ({roomCheckId}) => {
    const {bookingSetId, locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {
        locationRef,
        permission: PERMISSIONS.EDIT_ROOM_CHECK
      },
      () =>
        this.props.openModal(
          MODALS.ARRIVAL_ROOM_CHECK_DETAIL_MODAL,
          {
            roomCheckId,
            bookingSetId,
            locationRef
          },
          true
        )
    );
  };

  openDepartureRoomCheckDetailModal = ({roomCheckId}) => {
    const {bookingSetId, locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {
        locationRef,
        permission: PERMISSIONS.EDIT_ROOM_CHECK
      },
      () =>
        this.props.openModal(
          MODALS.DEPARTURE_ROOM_CHECK_DETAIL_MODAL,
          {
            roomCheckId,
            bookingSetId
          },
          true
        )
    );
  };

  openCreateChargeModal = ({bookingId, tenancyId}) => {
    this.props.openModal(BASE_MODALS.ONE_OFF_CHARGE, {
      bookingId,
      tenancyId
    });
  };

  openConfirmAddDepartureRoomCheckModal = () => {
    this.props.openModal(MODALS.CONFIRM_ADD_DEPARTURE_ROOM_CHECK);
  };

  render() {
    const {
      match: {params: {locationRef, bookingSetId}},
      bookingSetIsNotInThisLocation,
      bookingSet,
      financeItems,
      financeTotals,
      financeItemsAreLoading,
      modals: {
        bookingCreateModalOpened,
        invoiceDetailModalOpened,
        creditNoteDetailModalOpened,
        paymentDetailModalOpened,
        overpaymentDetailModalOpened,
        refundDetailModalOpened,
        invoiceCreateModalOpened,
        creditNoteCreateModalOpened,
        overpaymentCreateModalOpened,
        gocardlessCustomerDetailModalOpened,
        gocardlessPaymentDetailModalOpened,
        initiateGoCardlessConfirmModalOpened,
        cancelGocardlessCustomerModalOpened,
        confirmationModalOpened,
        selectBookingModalOpened,
        bulkInvoiceVoidModalOpened,
        confirmPaymentModal,
        addArrivalRoomCheckModalOpened,
        addDepartureRoomCheckModalOpened,
        arrivalRoomCheckDetailModalOpened,
        confirmAddDepartureRoomCheckModalOpened,
        oneOffChargeModalOpened,
        departureRoomCheckDetailModalOpened,
        depositSummaryDetailModalOpened
      },
      depositFinanceItems,
      depositFinanceItemsAreLoading,
      depositFinanceTotals,
      arrivalRoomChecks,
      departureRoomChecks,
      depositSummary,
      roomChecksTimeline
    } = this.props;

    const bookingTypes =
      !_.isEmpty(bookingSet) &&
      bookingSet.bookings.map(booking => booking.bookingType);

    const completedArrivalRoomChecks = arrivalRoomChecks.filter(
      roomCheck => _.get(roomCheck, 'status') == 'Completed'
    );
    return (
      <Page locationRef={locationRef} activeTab="bookingSets">
        {_.isEmpty(bookingSet) && <Loading />}
        {!_.isEmpty(bookingSet) &&
          bookingSetIsNotInThisLocation && (
            <ErrorBlock>No such booking set in this location</ErrorBlock>
          )}
        {!_.isEmpty(bookingSet) &&
          !bookingSetIsNotInThisLocation && (
            <DetailsContent className="BookingSetDetail">
              <DetailsContent.Header>
                <span className="BookingSetDetail__content__navigation">
                  Booking set {bookingSet.id}
                </span>
              </DetailsContent.Header>
              <DetailsContent.LeftSideBar>
                <PrimaryTenantBox
                  primaryTenant={bookingSet.tenant}
                  members={bookingSet.members}
                  currentRentAndOneOffBalance={
                    bookingSet.currentRentAndOneOffBalance
                  }
                  currentDepositBalance={bookingSet.currentDepositBalance}
                  startDate={bookingSet.startDate}
                  endDate={bookingSet.endDate}
                  stayDuration={bookingSet.stayDuration}
                  bookingTypes={_.uniq(bookingTypes)}
                  status={bookingSet.status}
                />
              </DetailsContent.LeftSideBar>
              <DetailsContent.Content>
                <TabNavigation
                  selectedTab={this.state.selectedTab}
                  selectTab={this.selectTab}
                />
                {this.state.selectedTab == BOOKINGS && (
                  <BookingsTable
                    bookings={bookingSet.bookings}
                    openCreateBookingModal={this.openCreateBookingModal}
                    redirectToBookingDetail={this.redirectToBookingDetail}
                  />
                )}

                {this.state.selectedTab == CHARGES_AND_PAYMENTS && (
                  <React.Fragment>
                    <Collapsible
                      name="Charges and payments"
                      rightHeader={BalanceHeader({
                        currentBalance: _.get(
                          financeTotals,
                          'highlightedBalance',
                          0.0
                        ),
                        totalBalance: _.get(financeTotals, 'totalBalance', 0.0)
                      })}>
                      <ChargesTable
                        data={financeItems}
                        handleRowClick={this.handleRowClick}
                        financeItemsAreLoading={financeItemsAreLoading}
                        openSelectBookingModal={this.openSelectBookingModal}
                        redirectToBookingDetail={this.redirectToBookingDetail}
                        openBulkInvoiceVoidModal={this.openBulkInvoiceVoidModal}
                      />
                    </Collapsible>

                    <Collapsible
                      name="Deposits"
                      rightHeader={BalanceHeader({
                        currentBalance: _.get(
                          depositFinanceTotals,
                          'highlightedBalance',
                          0.0
                        ),
                        totalBalance: _.get(
                          depositFinanceTotals,
                          'totalBalance',
                          0.0
                        )
                      })}>
                      <ChargesTable
                        data={depositFinanceItems}
                        handleRowClick={this.handleRowClick}
                        financeItemsAreLoading={depositFinanceItemsAreLoading}
                        openSelectBookingModal={this.openSelectBookingModal}
                        redirectToBookingDetail={this.redirectToBookingDetail}
                      />
                    </Collapsible>
                  </React.Fragment>
                )}

                {this.state.selectedTab == ROOM_CHECKS && (
                  <React.Fragment>
                    <RoomChecksTimeline
                      roomChecksTimeline={roomChecksTimeline}
                    />
                    <Collapsible
                      name="Deposit summary"
                      rightHeader={
                        <div className="depositSummaryStatusWrapper">
                          <DepositSummaryStatus
                            status={depositSummary.status}
                          />
                        </div>
                      }>
                      <DepositSummaryTable
                        depositSummary={depositSummary}
                        openDepositSummaryDetailModal={
                          this.openDepositSummaryDetailModal
                        }
                      />
                    </Collapsible>
                    <Collapsible
                      name="Arrival room check"
                      status={bookingSet.arrivalRoomCheckSectionStatus}>
                      <ArrivalRoomChecksTable
                        roomChecks={arrivalRoomChecks}
                        openAddArrivalRoomCheckModal={
                          this.openAddArrivalRoomCheckModal
                        }
                        openArrivalRoomCheckDetailModal={
                          this.openArrivalRoomCheckDetailModal
                        }
                      />
                    </Collapsible>
                    <Collapsible
                      name="Departure room check"
                      status={bookingSet.departureRoomCheckSectionStatus}>
                      <DepartureRoomChecksTable
                        roomChecks={departureRoomChecks}
                        disableAddButton={_.isEmpty(completedArrivalRoomChecks)}
                        openAddDepartureRoomCheckModal={
                          this.openAddDepartureRoomCheckModal
                        }
                        openDepartureRoomCheckDetailModal={
                          this.openDepartureRoomCheckDetailModal
                        }
                        openInvoiceDetailModal={this.openInvoiceDetailModal}
                      />
                    </Collapsible>
                  </React.Fragment>
                )}
                {bookingCreateModalOpened && (
                  <BookingCreateModal
                    locationRef={locationRef}
                    createBookingExtraActions={this.createBookingExtraActions}
                  />
                )}
                {invoiceDetailModalOpened && (
                  <InvoiceDetailModal
                    refetchExtraActions={this.financeRefetchExtraActions}
                    delayExtraActions={this.financeDelayedExtraActions}
                    openConfirmPaymentModal={this.openConfirmPaymentModal}
                  />
                )}
                {creditNoteDetailModalOpened && (
                  <CreditNoteDetailModal
                    refetchExtraActions={this.financeRefetchExtraActions}
                    delayExtraActions={this.financeDelayedExtraActions}
                  />
                )}
                {paymentDetailModalOpened && (
                  <PaymentDetailModal
                    refetchExtraActions={this.financeRefetchExtraActions}
                    delayExtraActions={this.financeDelayedExtraActions}
                  />
                )}
                {overpaymentDetailModalOpened && (
                  <OverpaymentDetailModal
                    refetchExtraActions={this.financeRefetchExtraActions}
                    delayExtraActions={this.financeDelayedExtraActions}
                  />
                )}
                {refundDetailModalOpened && (
                  <RefundDetailModal
                    refetchExtraActions={this.financeRefetchExtraActions}
                    delayExtraActions={this.financeDelayedExtraActions}
                  />
                )}
                {invoiceCreateModalOpened && (
                  <InvoiceCreateModal
                    closeModalSuccessExtraActions={() =>
                      closeModal(MODALS.SELECT_BOOKING)
                    }
                    refetchExtraActions={this.financeRefetchExtraActions}
                    delayExtraActions={this.financeDelayedExtraActions}
                  />
                )}
                {creditNoteCreateModalOpened && (
                  <CreditNoteCreateModal
                    closeModalSuccessExtraActions={() =>
                      closeModal(MODALS.SELECT_BOOKING)
                    }
                    refetchExtraActions={this.financeRefetchExtraActions}
                    delayExtraActions={this.financeDelayedExtraActions}
                  />
                )}
                {overpaymentCreateModalOpened && (
                  <OverpaymentCreateModal
                    closeModalSuccessExtraActions={() =>
                      closeModal(MODALS.SELECT_BOOKING)
                    }
                    refetchExtraActions={this.financeRefetchExtraActions}
                    delayExtraActions={this.financeDelayedExtraActions}
                  />
                )}
                {gocardlessCustomerDetailModalOpened && (
                  <GocardlessCustomerDetailModal />
                )}
                {gocardlessPaymentDetailModalOpened && (
                  <GocardlessPaymentDetailModal
                    refetchExtraActions={() =>
                      fetchBookingSetsFinanceData({bookingSetId})
                    }
                  />
                )}
                {initiateGoCardlessConfirmModalOpened && (
                  <InitiateGoCardlessPaymentModal />
                )}
                {cancelGocardlessCustomerModalOpened && (
                  <CancelGocardlessCustomerModal />
                )}
                {confirmationModalOpened && <ConfirmationModal />}
                {selectBookingModalOpened && (
                  <SelectBookingModal
                    openCreateItemModal={this.openCreateItemModal}
                  />
                )}
                {bulkInvoiceVoidModalOpened && (
                  <BulkInvoiceVoidModal
                    redirectToBookingDetail={this.redirectToBookingDetail}
                    openInvoiceDetailModal={this.openInvoiceDetailModal}
                    refetchExtraActions={
                      this.refetchChargesAndPaymentsExtraActions
                    }
                  />
                )}
                {addArrivalRoomCheckModalOpened && <AddArrivalRoomCheckModal />}
                {addDepartureRoomCheckModalOpened && (
                  <AddDepartureRoomCheckModal
                    openCreateChargeModal={this.openCreateChargeModal}
                    openConfirmAddDepartureRoomCheckModal={
                      this.openConfirmAddDepartureRoomCheckModal
                    }
                  />
                )}

                {arrivalRoomCheckDetailModalOpened && (
                  <ArrivalRoomCheckDetailModal />
                )}

                {confirmPaymentModal && <ConfirmPaymentModal />}

                {oneOffChargeModalOpened && (
                  <OneOffChargeModal
                    locationRef={locationRef}
                    openConfirmChargeModal={
                      this.openConfirmAddDepartureRoomCheckModal
                    }
                  />
                )}

                {confirmAddDepartureRoomCheckModalOpened && (
                  <ConfirmAddDepartureRoomCheckModal />
                )}

                {departureRoomCheckDetailModalOpened && (
                  <DepartureRoomCheckDetailModal locationRef={locationRef} />
                )}
                {depositSummaryDetailModalOpened && (
                  <DepositSummaryDetailModal depositSummary={depositSummary} />
                )}
              </DetailsContent.Content>
            </DetailsContent>
          )}
      </Page>
    );
  }
}

const mapStateToProps = (state, props) => {
  const {locationRef} = props.match.params;
  const bookingSet = selectBookingSet(state);

  const bookingSetIsNotInThisLocation =
    _.get(bookingSet, 'bookings[0].locationRef') !== locationRef;

  return {
    bookingSet,
    bookingSetIsNotInThisLocation,
    bookingSetIsLoading: selectBookingSetIsLoading(state),
    financeItems: selectBookingSetsFinanceItems(state),
    financeItemsAreLoading: selectBookingSetsFinanceDataIsLoading(state),
    financeTotals: selectBookingSetsFinanceTotals(state),
    modals: {
      bookingCreateModalOpened: isModalOpen(state, BASE_MODALS.CREATE_BOOKING),
      invoiceDetailModalOpened: isModalOpen(
        state,
        TENANCY_MODALS.INVOICE_DETAIL
      ),
      creditNoteDetailModalOpened: isModalOpen(
        state,
        TENANCY_MODALS.CREDIT_NOTE_DETAIL
      ),
      paymentDetailModalOpened: isModalOpen(
        state,
        TENANCY_MODALS.PAYMENT_DETAIL
      ),
      overpaymentDetailModalOpened: isModalOpen(
        state,
        TENANCY_MODALS.OVERPAYMENT_DETAIL
      ),
      refundDetailModalOpened: isModalOpen(state, TENANCY_MODALS.REFUND_DETAIL),
      invoiceCreateModalOpened: isModalOpen(
        state,
        TENANCY_MODALS.INVOICE_CREATE
      ),
      creditNoteCreateModalOpened: isModalOpen(
        state,
        TENANCY_MODALS.CREDIT_NOTE_CREATE
      ),
      overpaymentCreateModalOpened: isModalOpen(
        state,
        TENANCY_MODALS.OVERPAYMENT_CREATE
      ),
      gocardlessCustomerDetailModalOpened: isModalOpen(
        state,
        TENANCY_MODALS.GOCARDLESS_CUSTOMER_DETAIL
      ),
      gocardlessPaymentDetailModalOpened: isModalOpen(
        state,
        TENANCY_MODALS.GOCARDLESS_PAYMENT_DETAIL
      ),
      initiateGoCardlessConfirmModalOpened: isModalOpen(
        state,
        TENANCY_MODALS.INITIATE_GOCARDLESS_PAYMENT
      ),
      cancelGocardlessCustomerModalOpened: isModalOpen(
        state,
        TENANCY_MODALS.CANCEL_GOCARDLESS_CUSTOMER
      ),
      confirmationModalOpened: isModalOpen(state, BASE_MODALS.CONFIRM),
      selectBookingModalOpened: isModalOpen(state, MODALS.SELECT_BOOKING),
      bulkInvoiceVoidModalOpened: isModalOpen(state, MODALS.BULK_INVOICE_VOID),
      confirmPaymentModal: isModalOpen(state, TENANCY_MODALS.CONFIRM_PAYMENT),
      addArrivalRoomCheckModalOpened: isModalOpen(
        state,
        MODALS.ADD_ARRIVAL_ROOM_CHECK
      ),
      addDepartureRoomCheckModalOpened: isModalOpen(
        state,
        MODALS.ADD_DEPARTURE_ROOM_CHECK
      ),
      arrivalRoomCheckDetailModalOpened: isModalOpen(
        state,
        MODALS.ARRIVAL_ROOM_CHECK_DETAIL_MODAL
      ),
      confirmAddDepartureRoomCheckModalOpened: isModalOpen(
        state,
        MODALS.CONFIRM_ADD_DEPARTURE_ROOM_CHECK
      ),
      oneOffChargeModalOpened: isModalOpen(state, BASE_MODALS.ONE_OFF_CHARGE),
      departureRoomCheckDetailModalOpened: isModalOpen(
        state,
        MODALS.DEPARTURE_ROOM_CHECK_DETAIL_MODAL
      ),
      depositSummaryDetailModalOpened: isModalOpen(
        state,
        MODALS.DEPOSIT_SUMMARY_DETAIL_MODAL
      )
    },
    depositFinanceItems: selectBookingSetDepositFinanceItems(state),
    depositFinanceItemsAreLoading: selectBookingSetDepositFinanceDataIsLoading(
      state
    ),
    depositFinanceTotals: selectBookingSetDepositFinanceTotals(state),
    arrivalRoomChecks: selectArrivalRoomChecksForBookingSet(state),
    departureRoomChecks: selectDepartureRoomChecksForBookingSet(state),
    depositSummary: selectDepositSummary(state),
    roomChecksTimeline: selectRoomChecksTimeline(state)
  };
};

const mapDispatchToProps = {
  openModal,
  requiresPermission,
  fetchBookingSet,
  fetchBookingSetsFinanceData,
  push,
  fetchBookingSetDepositFinanceData,
  fetchArrivalRoomChecks,
  fetchDepartureRoomChecks,
  fetchDepositSummary,
  fetchRoomChecksTimeline
};

export default connect(mapStateToProps, mapDispatchToProps)(BookingSetDetail);
